import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import PolylineIcon from '@mui/icons-material/Polyline';

const Sliders = ({
  radius,
  setRadius,
  altitude,
  setAltitude,
  maxAltitude,
  geometryType,
  setGeometryType,
  polygonPoints,
  onCompletePolygon,
  onClearPolygon,
  circleGeoJSON,
  HomeStyles,
  polygonError
}) => {

  // Determine if polygon is completed (no points but geometry exists)
  const isPolygonCompleted = geometryType === 'polygon' && circleGeoJSON && polygonPoints.length === 0;

  return (
    <div className={HomeStyles.sliders}>
      <div className={HomeStyles.radiusSlider}>
        {/* Geometry Type Selector with Icons */}
        <div style={{ marginBottom: '16px' }}>
          <label className={HomeStyles.s_bar_subtopics} style={{ display: 'block', marginBottom: '8px' }}>
            Geofence Type:
          </label>
          <ToggleButtonGroup
            value={geometryType}
            exclusive
            onChange={(event, newType) => {
              if (newType !== null) {
                setGeometryType(newType);
              }
            }}
            aria-label="geofence type"
            sx={{
              width: '100%',
              '& .MuiToggleButton-root': {
                flex: 1,
                padding: '8px 16px',
                fontSize: '13px',
                textTransform: 'none',
                border: '1px solid #ddd',
                '&.Mui-selected': {
                  backgroundColor: '#088',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#066',
                  },
                },
              },
            }}
          >
            <ToggleButton value="circle" aria-label="circle geofence">
              <RadioButtonCheckedIcon sx={{ mr: 1, fontSize: '18px' }} />
              Circle
            </ToggleButton>
            <ToggleButton value="polygon" aria-label="polygon geofence">
              <PolylineIcon sx={{ mr: 1, fontSize: '18px' }} />
              Custom Polygon
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {/* Radius Slider - Only show for circle */}
        {geometryType === 'circle' && (
          <>
            <label htmlFor="radius" className={HomeStyles.s_bar_subtopics}>
              Action Zone: {radius} ft <div className={HomeStyles.Max}>Max - 3000ft</div>
            </label>
            <input
              type="range"
              id="radius"
              min="10"
              max="3000"
              value={radius}
              onChange={(e) => {
                const newRadius = parseInt(e.target.value);
                setRadius(newRadius);
                setAltitude(0);
              }}
              aria-label="Action Zone Radius"
              aria-valuemin="10"
              aria-valuemax="3000"
              aria-valuenow={radius}
              aria-valuetext={`${radius} feet`}
            />
          </>
        )}

        {/* Polygon Drawing Controls - Only show for polygon */}
        {geometryType === 'polygon' && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555', fontWeight: '500' }}>
              {isPolygonCompleted
                ? 'Polygon completed! Click "Clear Polygon" to draw a new one.'
                : polygonPoints.length === 0
                ? 'Click on the map to start drawing your polygon'
                : `${polygonPoints.length} point${polygonPoints.length > 1 ? 's' : ''} added. ${polygonPoints.length < 3 ? 'Need at least 3 points to complete.' : 'Ready to complete!'}`
              }
            </p>
            {polygonError && (
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#d32f2f', fontWeight: '500', whiteSpace: 'pre-line' }}>
                {polygonError}
              </p>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Show Complete button only while drawing */}
              {!isPolygonCompleted && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCompletePolygon();
                  }}
                  disabled={polygonPoints.length < 3}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    backgroundColor: polygonPoints.length >= 3 ? '#4CAF50' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: polygonPoints.length >= 3 ? 'pointer' : 'not-allowed',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (polygonPoints.length >= 3) {
                      e.target.style.backgroundColor = '#45a049';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (polygonPoints.length >= 3) {
                      e.target.style.backgroundColor = '#4CAF50';
                    }
                  }}
                >
                  Complete Polygon
                </button>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClearPolygon();
                }}
                disabled={polygonPoints.length === 0 && !isPolygonCompleted}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: (polygonPoints.length > 0 || isPolygonCompleted) ? '#f44336' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (polygonPoints.length > 0 || isPolygonCompleted) ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (polygonPoints.length > 0 || isPolygonCompleted) {
                    e.target.style.backgroundColor = '#da190b';
                  }
                }}
                onMouseLeave={(e) => {
                  if (polygonPoints.length > 0 || isPolygonCompleted) {
                    e.target.style.backgroundColor = '#f44336';
                  }
                }}
              >
                {isPolygonCompleted ? 'Clear Polygon' : 'Clear Points'}
              </button>
            </div>
          </div>
        )}

        {/* Altitude Slider - Always show */}
        <label htmlFor="altitude" className={HomeStyles.s_bar_subtopics}>
          Altitude (AGL): {altitude} ft <div className={HomeStyles.Max}>Max - {maxAltitude}ft</div>
        </label>
        <input
          type="range"
          id="altitude"
          min="0"
          max={maxAltitude}
          value={altitude}
          onChange={(e) => setAltitude(parseInt(e.target.value))}
          aria-label="Altitude Above Ground Level"
          aria-valuemin="0"
          aria-valuemax={maxAltitude}
          aria-valuenow={altitude}
          aria-valuetext={`${altitude} feet above ground level`}
        />
      </div>
    </div>
  );
};

export default Sliders;